import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface Tab {
    key: string;
    label: string;
}

interface TimesheetTabsProps {
    tabs: Tab[];
    activeTab: string;
    onTabChange: (tabKey: string) => void;
}

/* Inline icons keyed by tab — pencil for creating, document for
   invoiced, clock for worked-hours. currentColor so they inherit
   the active/inactive text colour. */
const getTabIcon = (key: string): React.ReactNode => {
    switch (key) {
        case 'creating-timesheet':
            return (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                        d="M2 11.5V14h2.5l7.4-7.4-2.5-2.5L2 11.5Zm10.7-7.7a.7.7 0 0 0 0-1l-1.5-1.5a.7.7 0 0 0-1 0L9 2.5l2.5 2.5 1.2-1.2Z"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        case 'reports':
            return (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                        d="M4 1.5h6l3 3v9a.5.5 0 0 1-.5.5h-8.5a.5.5 0 0 1-.5-.5V2a.5.5 0 0 1 .5-.5Z"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M5.5 8.5h5M5.5 11h5M5.5 6h3"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                    />
                </svg>
            );
        case 'worked-hours':
            return (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle
                        cx="8"
                        cy="8"
                        r="6"
                        stroke="currentColor"
                        strokeWidth="1.4"
                    />
                    <path
                        d="M8 4.5V8l2.2 1.3"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        default:
            return null;
    }
};

const TimesheetTabs: React.FC<TimesheetTabsProps> = ({ tabs, activeTab, onTabChange }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
    const [isAnimated, setIsAnimated] = useState<boolean>(false);

    /* Measure the active tab and place the sliding indicator behind it.
       useLayoutEffect runs synchronously before paint so the indicator
       is in place on the very first render — no flash. */
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const activeBtn = container.querySelector<HTMLButtonElement>(
            `[data-tab-key="${activeTab}"]`
        );
        if (!activeBtn) return;
        setIndicator({
            left: activeBtn.offsetLeft,
            width: activeBtn.offsetWidth,
        });
    }, [activeTab, tabs]);

    /* Re-measure on resize so the indicator stays aligned. */
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const handleResize = () => {
            const activeBtn = container.querySelector<HTMLButtonElement>(
                `[data-tab-key="${activeTab}"]`
            );
            if (!activeBtn) return;
            setIndicator({
                left: activeBtn.offsetLeft,
                width: activeBtn.offsetWidth,
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [activeTab]);

    /* Flip "is-animated" on after first paint so the indicator doesn't
       slide in from x=0 on initial mount — only real tab switches animate. */
    useEffect(() => {
        const id = window.requestAnimationFrame(() => setIsAnimated(true));
        return () => window.cancelAnimationFrame(id);
    }, []);

    return (
        <div
            ref={containerRef}
            className={`ts-pill-tabs${isAnimated ? ' is-animated' : ''}`}
            role="tablist"
        >
            <span
                className="ts-pill-tabs__indicator"
                aria-hidden="true"
                style={{
                    transform: `translateX(${indicator.left}px)`,
                    width: indicator.width,
                }}
            />
            {tabs.map((tab) => {
                const isActive = activeTab === tab.key;
                const icon = getTabIcon(tab.key);
                return (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        data-tab-key={tab.key}
                        className={`ts-pill-tabs__tab${isActive ? ' is-active' : ''}`}
                        onClick={() => onTabChange(tab.key)}
                    >
                        {icon}
                        <span>{tab.label}</span>
                    </button>
                );
            })}
        </div>
    );
};

export default TimesheetTabs;
